import { API_BASE } from '../apiBase';
// src/employees/EmployeeTimecard.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, startOfMonth, subMonths, addMonths, isSameDay, isToday } from 'date-fns';
import { Calendar } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// date-fns localizer
import { dateFnsLocalizer } from 'react-big-calendar';
import formatFns from 'date-fns/format';
import parseFns from 'date-fns/parse';
import startOfWeekFns from 'date-fns/startOfWeek';
import getDayFns from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';

const locales = { 'en-US': enUS };

const localizer = dateFnsLocalizer({
  format: formatFns,
  parse: parseFns,
  startOfWeek: startOfWeekFns,
  getDay: getDayFns,
  locales,
});

function EmployeeTimecard() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [employee, setEmployee] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState('month');
  const [viewRange, setViewRange] = useState('1m');
  const [loading, setLoading] = useState(true);

  // Modal for adding / editing time blocks
  const [showModal, setShowModal] = useState(false);
  const [modalDate, setModalDate] = useState(null);
  const [timeBlocks, setTimeBlocks] = useState([{ start: '', end: '', note: '' }]);
  // editMode: 'new' (add to a day) | 'editDay' (overwrite a day) | 'editBlock' (one segment)
  const [editMode, setEditMode] = useState('new');
  const [editEntryId, setEditEntryId] = useState(null);
  const [editSegIndex, setEditSegIndex] = useState(null);
  // The calendar cell the user clicked (highlight + shows the "+" add affordance).
  const [selectedCell, setSelectedCell] = useState(null);

  const role = localStorage.getItem('role') || 'employee';
  const isSuperAdmin = role === 'superadmin';

  // "HH:mm" string from a Date (for loading existing segments into the editor).
  const hhmm = (d) => format(new Date(d), 'HH:mm');

  useEffect(() => {
    fetchEmployeeAndTimeEntries();
  }, [id]);

  const fetchEmployeeAndTimeEntries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const empRes = await axios.get(`${API_BASE}/employees/${id}`, {
        headers: { Authorization: token },
      });
      setEmployee(empRes.data);

      const entriesRes = await axios.get(`${API_BASE}/time-entries/employee/${id}`, {
        headers: { Authorization: token },
      });
      setTimeEntries(entriesRes.data || []);
    } catch (err) {
      console.error('Failed to load timecard data:', err);
      alert('Failed to load timecard data');
    } finally {
      setLoading(false);
    }
  };

  const events = useMemo(() => {
    let filtered = [...timeEntries];

    if (viewRange !== 'all') {
      const monthsBack = { '1m': 1, '3m': 3, '6m': 6, '12m': 12 }[viewRange] || 1;
      const startDate = subMonths(new Date(), monthsBack);
      filtered = filtered.filter(e => new Date(e.date) >= startDate);
    }

    const allEvents = [];

    filtered.forEach(entry => {
      if (entry.segments?.length > 0) {
        entry.segments.forEach((seg, idx) => {
          const start = new Date(seg.start);
          const end = new Date(seg.end);

          let title = format(start, 'h:mm a') + ' – ' + format(end, 'h:mm a');
          if (seg.note) title += ` (${seg.note})`;

          allEvents.push({
            id: `${entry._id}-seg${idx}`,
            title,
            start,
            end,
            resource: entry,
            entryId: entry._id,
            segIndex: idx,
            color: '#3b82f6',
            hasAdminNote: entry.adminNotes?.length > 0,
          });
        });
      }
    });

    return allEvents;
  }, [timeEntries, viewRange]);

  // Find the saved day-entry for a given calendar date (if any).
  const entryForDate = (date) =>
    timeEntries.find(e => isSameDay(new Date(e.date), date));

  // Single click an empty cell = highlight it (and show the "+"). Double-click a
  // cell = edit ALL of that day's blocks at once.
  const handleSelectSlot = ({ start, action }) => {
    if (!isSuperAdmin) return;
    if (action === 'doubleClick') {
      openEditDay(start);
    } else {
      setSelectedCell(start);
    }
  };

  // Add brand-new blocks to a day (the "+" button).
  const openNewModal = (date) => {
    setModalDate(date);
    setEditMode('new');
    setEditEntryId(null);
    setEditSegIndex(null);
    setTimeBlocks([{ start: '', end: '', note: '' }]);
    setShowModal(true);
  };

  // Edit every block on a day (double-click the cell). Loads existing blocks;
  // saving OVERWRITES the day. Empty day behaves like a fresh add.
  const openEditDay = (date) => {
    const entry = entryForDate(date);
    setModalDate(date);
    if (entry && entry.segments?.length) {
      setEditMode('editDay');
      setEditEntryId(entry._id);
      setTimeBlocks(entry.segments.map(s => ({ start: hhmm(s.start), end: hhmm(s.end), note: s.note || '' })));
    } else {
      setEditMode('new');
      setEditEntryId(null);
      setTimeBlocks([{ start: '', end: '', note: '' }]);
    }
    setEditSegIndex(null);
    setShowModal(true);
  };

  // Edit ONE block (single-click an event). Other blocks on the day are untouched.
  const openEditBlock = (event) => {
    if (!isSuperAdmin) return;
    setModalDate(new Date(event.start));
    setEditMode('editBlock');
    setEditEntryId(event.entryId);
    setEditSegIndex(event.segIndex);
    setTimeBlocks([{ start: hhmm(event.start), end: hhmm(event.end), note: event.resource?.segments?.[event.segIndex]?.note || '' }]);
    setShowModal(true);
  };

  const addTimeBlock = () => {
    setTimeBlocks([...timeBlocks, { start: '', end: '', note: '' }]);
  };

  const updateTimeBlock = (index, field, value) => {
    const updated = [...timeBlocks];
    updated[index][field] = value;
    setTimeBlocks(updated);
  };

  const removeTimeBlock = (index) => {
    setTimeBlocks(timeBlocks.filter((_, i) => i !== index));
  };

  const saveTimeBlocks = async () => {
    if (timeBlocks.length === 0) return alert('Add at least one time block');
    for (const block of timeBlocks) {
      if (!block.start || !block.end) return alert('All blocks need start and end times');
    }

    const dayStr = format(modalDate, 'yyyy-MM-dd');
    const toIso = (b) => ({ start: `${dayStr}T${b.start}`, end: `${dayStr}T${b.end}`, note: b.note });

    try {
      const token = localStorage.getItem('token');

      if (editMode === 'editBlock') {
        // Overwrite just the one segment that was clicked.
        const b = timeBlocks[0];
        await axios.put(
          `${API_BASE}/time-entries/${editEntryId}/segment/${editSegIndex}`,
          toIso(b),
          { headers: { Authorization: token } }
        );
      } else {
        // 'new' = append to the day; 'editDay' = replace the whole day.
        await axios.post(
          `${API_BASE}/time-entries/${id}/bulk`,
          { date: modalDate, blocks: timeBlocks.map(toIso), replace: editMode === 'editDay' },
          { headers: { Authorization: token } }
        );
      }

      setShowModal(false);
      fetchEmployeeAndTimeEntries();
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save: ' + (err.response?.data?.error || err.message || 'Server error'));
      setShowModal(false);
    }
  };

  // Delete the single block being edited (from the edit-block modal).
  const deleteEditingBlock = async () => {
    if (editMode !== 'editBlock' || !editEntryId || editSegIndex == null) return;
    if (!window.confirm('Delete this time block?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/time-entries/${editEntryId}/segment/${editSegIndex}`, { headers: { Authorization: token } });
      setShowModal(false);
      fetchEmployeeAndTimeEntries();
    } catch (err) {
      alert('Failed to delete block.');
    }
  };

  // Month-view date cell: show a "+" to the left of the date number on the
  // currently-highlighted cell (Super Admin only) → opens the add-blocks modal.
  const calendarComponents = useMemo(() => ({
    month: {
      dateHeader: ({ label, date }) => {
        const isSel = selectedCell && isSameDay(date, selectedCell);
        return (
          <div className="flex items-center justify-end gap-1">
            {isSuperAdmin && isSel && (
              <button
                type="button"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); openNewModal(date); }}
                title="Add time blocks"
                className="w-5 h-5 flex items-center justify-center rounded-full bg-indigo-600 hover:bg-indigo-500 text-white text-sm leading-none shadow"
              >
                +
              </button>
            )}
            <span>{label}</span>
          </div>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [selectedCell, isSuperAdmin]);

  if (loading) {
    return <div className="min-h-screen bg-gray-950 p-8 text-zinc-100 flex items-center justify-center">Loading timecard...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-6 space-y-8">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3 border-b border-gray-800 pb-6 max-md:pb-4">
        <div>
          <h1 className="text-3xl max-md:text-xl font-bold">{employee?.name || 'Employee'}'s Timecard</h1>
          <p className="text-zinc-400 mt-1 max-md:text-sm">
            {employee?.role || 'Role'} • {employee?.employeeNumber || 'N/A'}
          </p>
        </div>
        <div className="flex gap-4 max-md:gap-2">
          {/* Dev button for employee mobile view */}
          <button
            onClick={() => navigate(`/employees/${id}/mobile-timecard`)}
            className="px-6 py-3 max-md:px-2.5 max-md:py-1 max-md:text-[11px] bg-purple-700 hover:bg-purple-600 rounded-xl max-md:rounded-md font-medium transition-all"
          >
            Employee Dev TimeCard
          </button>
          <button
            onClick={() => navigate('/employees')}
            className="px-6 py-3 max-md:px-2.5 max-md:py-1 max-md:text-[11px] bg-gray-700 hover:bg-gray-600 rounded-xl max-md:rounded-md font-medium transition-all"
          >
            Back to Employees
          </button>
        </div>
      </div>

      {/* Calendar Container */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-6">
            <h3 className="text-xl font-semibold">Calendar</h3>
            <div className="flex gap-3 max-md:hidden">
              {['1m', '3m', '6m', '12m', 'all'].map(range => (
                <button
                  key={range}
                  onClick={() => setViewRange(range)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    viewRange === range
                      ? 'bg-indigo-600 text-white shadow-md ring-1 ring-indigo-500'
                      : 'bg-gray-800 hover:bg-gray-700 text-zinc-300'
                  }`}
                >
                  {range === 'all' ? 'All Time' : `${range.replace('m', ' Month')}${range !== '1m' ? 's' : ''}`}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2 max-md:hidden">
            <button
              onClick={() => setCalendarView('month')}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                calendarView === 'month' ? 'bg-indigo-600 text-white' : 'bg-gray-800 hover:bg-gray-700 text-zinc-300'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => { if (selectedCell) setCurrentDate(selectedCell); setCalendarView('week'); }}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                calendarView === 'week' ? 'bg-indigo-600 text-white' : 'bg-gray-800 hover:bg-gray-700 text-zinc-300'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => { if (selectedCell) setCurrentDate(selectedCell); setCalendarView('day'); }}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                calendarView === 'day' ? 'bg-indigo-600 text-white' : 'bg-gray-800 hover:bg-gray-700 text-zinc-300'
              }`}
            >
              Day
            </button>
            <button
              onClick={() => setCalendarView('agenda')}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                calendarView === 'agenda' ? 'bg-indigo-600 text-white' : 'bg-gray-800 hover:bg-gray-700 text-zinc-300'
              }`}
            >
              Agenda
            </button>
          </div>
        </div>

        <div className="text-sm text-zinc-400 mb-4 max-md:hidden">
          Showing {events.length} of {timeEntries.length} events
        </div>

        {/* MOBILE: the month as day cards — no grid to squint at. */}
        <div className="md:hidden">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setCurrentDate(d => subMonths(d, 1))} className="px-3 py-1.5 bg-gray-800 rounded-lg text-xs font-semibold text-zinc-300">‹ Prev</button>
            <span className="text-sm font-bold text-zinc-100">{format(currentDate, 'MMMM yyyy')}</span>
            <button onClick={() => setCurrentDate(d => addMonths(d, 1))} className="px-3 py-1.5 bg-gray-800 rounded-lg text-xs font-semibold text-zinc-300">Next ›</button>
          </div>
          {(() => {
            const monthEntries = timeEntries
              .filter(e => {
                const d = new Date(e.date);
                return d.getFullYear() === currentDate.getFullYear() && d.getMonth() === currentDate.getMonth();
              })
              .sort((a, b) => new Date(a.date) - new Date(b.date));
            if (!monthEntries.length) return <p className="text-center text-zinc-600 text-sm py-8">No time entries this month.</p>;
            return monthEntries.map(entry => {
              const segs = entry.segments || [];
              const hours = segs.reduce((s, sg) => s + Math.max(0, (new Date(sg.end) - new Date(sg.start)) / 3600000), 0);
              const d = new Date(entry.date);
              return (
                <div
                  key={entry._id}
                  onClick={() => { if (isSuperAdmin) openEditDay(d); }}
                  className={`bg-gray-950/50 border rounded-xl p-3 mb-2 ${isToday(d) ? 'border-indigo-500/50' : 'border-gray-800'} ${isSuperAdmin ? 'active:bg-gray-800/60' : ''} ${entry.adminNotes?.length ? 'border-l-4 border-l-red-500' : ''}`}
                >
                  <div className="flex justify-between items-center">
                    <span className={`text-sm font-bold ${isToday(d) ? 'text-indigo-300' : 'text-zinc-200'}`}>
                      {format(d, 'EEE, MMM d')}{isToday(d) ? ' · Today' : ''}
                    </span>
                    <span className="text-sm font-black text-emerald-400">{hours.toFixed(2)} hrs</span>
                  </div>
                  <div className="mt-1.5 space-y-1">
                    {segs.map((sg, i) => (
                      <div key={i} className="text-xs text-zinc-400">
                        {format(new Date(sg.start), 'h:mm a')} – {format(new Date(sg.end), 'h:mm a')}
                        {sg.note ? <span className="text-zinc-600"> · {sg.note}</span> : null}
                      </div>
                    ))}
                  </div>
                </div>
              );
            });
          })()}
          {isSuperAdmin && (
            <p className="text-[11px] text-zinc-600 mt-2">Tap a day to edit its time blocks.</p>
          )}
        </div>

        <div className="max-md:hidden">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600 }}
          date={currentDate}
          view={calendarView}
          onView={setCalendarView}
          onNavigate={setCurrentDate}
          onSelectEvent={openEditBlock}
          onSelectSlot={handleSelectSlot}
          selectable={isSuperAdmin}
          components={calendarComponents}
          eventPropGetter={event => ({
            style: {
              backgroundColor: event.color,
              border: event.hasAdminNote ? '2px solid #ef4444' : 'none',
              borderRadius: '6px',
              opacity: 0.92,
              minHeight: '50px',
              padding: '4px',
              fontSize: '14px',
              cursor: isSuperAdmin ? 'pointer' : 'default',
            },
          })}
          dayPropGetter={date => {
            const today = isToday(date);
            const isSel = selectedCell && isSameDay(date, selectedCell);
            return {
              style: {
                backgroundColor: isSel ? 'rgba(99,102,241,0.28)' : (today ? 'rgba(79, 70, 229, 0.18)' : 'transparent'),
                border: isSel ? '2px solid #818cf8' : (today ? '2px solid #4f46e5' : 'none'),
                borderRadius: (isSel || today) ? '8px' : '0',
                cursor: isSuperAdmin ? 'pointer' : 'default',
                transition: 'all 0.15s ease',
              },
            };
          }}
          toolbar={false}
        />
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-6 text-sm pt-6 border-t border-gray-800">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-600"></div> Regular Shift
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-500"></div> Sick
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-purple-600"></div> Vacation
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-yellow-500"></div> Late
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-red-500"></div> Has Admin Note
        </div>
      </div>

      {/* Superadmin Modal */}
      {isSuperAdmin && showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div className="bg-gray-900 rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700" style={{ scrollbarGutter: 'stable' }}>
            <h2 className="text-2xl font-bold mb-6">
              {editMode === 'editBlock'
                ? `Edit Time Block — ${format(modalDate, 'MMM d, yyyy')}`
                : editMode === 'editDay'
                ? `Edit All Blocks — ${format(modalDate, 'MMM d, yyyy')}`
                : `Add Time Blocks for ${format(modalDate, 'MMM d, yyyy')}`}
            </h2>

            {timeBlocks.length === 0 && (
              <p className="text-zinc-500 text-center mb-6">No time blocks yet — add one below</p>
            )}

            {timeBlocks.map((block, index) => (
              <div key={index} className="bg-gray-800 p-5 rounded-xl flex flex-col sm:flex-row gap-4 items-end mb-4">
                <div className="flex-1">
                  <label className="block text-sm text-zinc-400 mb-1">Start Time</label>
                  <TimeSelect
                    value={block.start}
                    onChange={v => updateTimeBlock(index, 'start', v)}
                    minTime={index > 0 ? timeBlocks[index - 1].end : ''}
                    minInclusive
                    placeholder="Start time"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm text-zinc-400 mb-1">End Time</label>
                  <TimeSelect
                    value={block.end}
                    onChange={v => updateTimeBlock(index, 'end', v)}
                    minTime={block.start}
                    durationFrom={block.start}
                    placeholder="End time"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm text-zinc-400 mb-1">Description / Note</label>
                  <input
                    type="text"
                    value={block.note}
                    onChange={e => updateTimeBlock(index, 'note', e.target.value)}
                    placeholder="e.g. Work, Lunch, Home Depot run"
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                </div>
                {editMode !== 'editBlock' && (
                  <button
                    onClick={() => removeTimeBlock(index)}
                    className="px-4 py-2 bg-red-900 hover:bg-red-800 text-white rounded-lg text-sm"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

            {editMode !== 'editBlock' && (
              <button
                onClick={addTimeBlock}
                className="w-full py-3 bg-indigo-700 hover:bg-indigo-600 rounded-xl font-medium text-white mt-4"
              >
                + Add New Time Block
              </button>
            )}

            <div className="flex justify-end gap-4 mt-8">
              {editMode === 'editBlock' && (
                <button
                  onClick={deleteEditingBlock}
                  className="px-6 py-3 bg-red-800 hover:bg-red-700 rounded-xl font-medium mr-auto"
                >
                  Delete Block
                </button>
              )}
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                onClick={saveTimeBlocks}
                disabled={timeBlocks.some(b => !b.start || !b.end)}
                className="px-6 py-3 bg-green-700 hover:bg-green-600 disabled:bg-green-900 disabled:cursor-not-allowed rounded-xl font-medium"
              >
                {editMode === 'editBlock' ? 'Save Block' : editMode === 'editDay' ? 'Save Changes' : 'Save All Blocks'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Searchable 15-minute time dropdown (Google-Calendar style). Self-contained so
// it can be reused or removed without touching the rest of the page.
//   value       : "HH:mm" (24h) or ''       — what gets stored
//   onChange    : (v) => void               — receives "HH:mm"
//   minTime     : optional "HH:mm"          — only offer times AFTER this
//   durationFrom: optional "HH:mm"          — show duration relative to this
// ---------------------------------------------------------------------------
function TimeSelect({ value, onChange, minTime, minInclusive = false, durationFrom, placeholder = 'Select time' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropUp, setDropUp] = useState(false);
  const [menuMaxH, setMenuMaxH] = useState(224);
  const boxRef = useRef(null);
  const listRef = useRef(null);

  // 24h "HH:mm" -> "h:mm AM/PM"
  const fmt = (v) => {
    if (!v || !/^\d{1,2}:\d{2}$/.test(v)) return v || '';
    const [h, m] = v.split(':').map(Number);
    const ampm = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  // Parse free-typed input to "HH:mm" so the user isn't boxed into 15-min slots.
  // Accepts: "7", "752", "1530", "7:52", "7:52pm", "3p", "12:05 am", etc.
  const parseTyped = (q) => {
    if (!q) return null;
    let s = q.trim().toLowerCase().replace(/\s+/g, '').replace(/\./g, '');
    let ap = null;
    if (s.endsWith('am')) { ap = 'am'; s = s.slice(0, -2); }
    else if (s.endsWith('pm')) { ap = 'pm'; s = s.slice(0, -2); }
    else if (s.endsWith('a')) { ap = 'am'; s = s.slice(0, -1); }
    else if (s.endsWith('p')) { ap = 'pm'; s = s.slice(0, -1); }
    let h, m;
    if (s.includes(':')) {
      const [hh, mm] = s.split(':');
      h = parseInt(hh, 10); m = parseInt(mm, 10);
    } else {
      if (!/^\d+$/.test(s)) return null;
      if (s.length <= 2) { h = parseInt(s, 10); m = 0; }
      else { m = parseInt(s.slice(-2), 10); h = parseInt(s.slice(0, -2), 10); }
    }
    if (isNaN(h) || isNaN(m)) return null;
    if (ap === 'pm' && h < 12) h += 12;
    if (ap === 'am' && h === 12) h = 0;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const options = useMemo(() => {
    const opts = [];
    for (let mins = 0; mins < 24 * 60; mins += 15) {
      const h = Math.floor(mins / 60);
      const mi = mins % 60;
      const v = `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`;
      const ampm = h < 12 ? 'AM' : 'PM';
      const h12 = h % 12 === 0 ? 12 : h % 12;
      opts.push({ v, label: `${h12}:${String(mi).padStart(2, '0')} ${ampm}` });
    }
    return opts;
  }, []);

  const labelFor = (v) => fmt(v) || v || '';

  const toMin = (v) => { const [h, m] = String(v).split(':').map(Number); return h * 60 + m; };

  const durationLabel = (optV) => {
    if (!durationFrom) return '';
    const diff = toMin(optV) - toMin(durationFrom);
    if (diff <= 0) return '';
    const hrs = Math.floor(diff / 60), mm = diff % 60;
    return `${hrs}h${mm ? ` ${mm}m` : ''}`;
  };

  // Only offer times at/after minTime. End fields are exclusive (end must be
  // AFTER its start); a later block's start is inclusive (it may begin exactly
  // when the previous block ended, e.g. shift → lunch).
  const baseOptions = useMemo(() => {
    if (!minTime) return options;
    const sm = toMin(minTime);
    return options.filter(o => minInclusive ? toMin(o.v) >= sm : toMin(o.v) > sm);
  }, [options, minTime, minInclusive]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/\s+/g, '');
    if (!q) return baseOptions;
    return baseOptions.filter(o =>
      o.label.toLowerCase().replace(/\s+/g, '').includes(q) || o.v.includes(q)
    );
  }, [baseOptions, query]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setQuery(''); } };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Size the menu to the room available INSIDE the card (its nearest scrollable
  // ancestor), and flip it upward when there isn't enough space below — so it
  // never spills past the card edge.
  useEffect(() => {
    if (!open || !boxRef.current) return;
    const inputRect = boxRef.current.getBoundingClientRect();
    let boundTop = 8, boundBottom = window.innerHeight - 8;
    let p = boxRef.current.parentElement;
    while (p) {
      const oy = window.getComputedStyle(p).overflowY;
      if (oy === 'auto' || oy === 'scroll') {
        const r = p.getBoundingClientRect();
        boundTop = r.top + 8;
        boundBottom = r.bottom - 8;
        break;
      }
      p = p.parentElement;
    }
    const spaceBelow = boundBottom - inputRect.bottom;
    const spaceAbove = inputRect.top - boundTop;
    const desired = 224;
    if (spaceBelow >= Math.min(desired, 140) || spaceBelow >= spaceAbove) {
      setDropUp(false);
      setMenuMaxH(Math.max(120, Math.min(desired, spaceBelow)));
    } else {
      setDropUp(true);
      setMenuMaxH(Math.max(120, Math.min(desired, spaceAbove)));
    }
  }, [open]);

  // On open: center the selected time if there is one; otherwise default the
  // scroll to 8:00 AM (unconstrained start) or the first valid option — scrolling
  // only the list itself, never the surrounding card.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const list = listRef.current;
    if (value) {
      const el = list.querySelector(`[data-v="${value}"]`);
      if (el) list.scrollTop = el.offsetTop - list.clientHeight / 2 + el.clientHeight / 2;
    } else if (!minTime) {
      const el = list.querySelector('[data-v="08:00"]');
      list.scrollTop = el ? el.offsetTop : 0;
    } else {
      list.scrollTop = 0;
    }
  }, [open, value, minTime]);

  const choose = (v) => { onChange(v); setOpen(false); setQuery(''); };

  return (
    <div ref={boxRef} className="relative">
      <input
        type="text"
        value={open ? query : labelFor(value)}
        placeholder={placeholder}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const typed = parseTyped(query);
            if (typed) choose(typed);
            else if (filtered.length) choose(filtered[0].v);
          } else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
        }}
        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-zinc-500"
      />
      {open && (() => {
        const typed = parseTyped(query);
        // Don't offer an exact time that's already a slot, or one at/before the
        // minimum (the block's start, or the previous block's end).
        const showExact = typed && !options.some(o => o.v === typed) && (!minTime || (minInclusive ? toMin(typed) >= toMin(minTime) : toMin(typed) > toMin(minTime)));
        return (
          <div ref={listRef} style={{ maxHeight: menuMaxH }} className={`absolute z-50 w-full overflow-y-auto bg-gray-800 border border-gray-600 rounded-lg shadow-xl ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
            {showExact && (
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); choose(typed); }}
                className="w-full text-left px-4 py-2 bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30 border-b border-gray-700"
              >
                <span className="block text-sm">Use {fmt(typed)} <span className="text-emerald-500/60">(exact)</span></span>
                {durationLabel(typed) && <span className="block text-[11px] text-emerald-500/70">{durationLabel(typed)}</span>}
              </button>
            )}
            {filtered.map(o => {
              const dur = durationLabel(o.v);
              return (
                <button
                  key={o.v}
                  type="button"
                  data-v={o.v}
                  onMouseDown={(e) => { e.preventDefault(); choose(o.v); }}
                  className={`w-full text-left px-4 py-2 hover:bg-indigo-600/30 ${value === o.v ? 'bg-indigo-600/20 text-indigo-300' : 'text-zinc-200'}`}
                >
                  <span className="block text-sm">{o.label}</span>
                  {dur && <span className="block text-[11px] text-zinc-500">{dur}</span>}
                </button>
              );
            })}
            {filtered.length === 0 && !showExact && (
              <div className="px-4 py-2 text-sm text-zinc-500">No match</div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

export default EmployeeTimecard;